import React, { useState, Suspense } from 'react'
import { Canvas } from '@react-three/fiber'
import { useContextBridge } from '@react-three/drei'
import * as THREE from 'three'
import { HelmetProvider } from 'react-helmet-async'
import { Perf } from 'r3f-perf'
import { createXRStore, XR } from '@react-three/xr'
import { Glasses, Gamepad2, Smartphone } from 'lucide-react'

import { SceneXyz3D } from './experience/SceneXyz3D.jsx'
import
{
  HtmlOverlay,
  ProgressLoader,
  SplashScreen,
  Seo,
  WebCrafterProvider,
  useWebCrafter,
  WebCrafterContext,
  isDebugMode,
  OrbitNavigation,
} from '@webcrafter/common'

const GL_CONFIG = {
  alpha: true,
  toneMapping: THREE.ACESFilmicToneMapping,
  toneMappingExposure: 1,
}

const SCENE_PATH = 'assets/scene.glb'
const store = createXRStore()

const TUTORIAL_STEPS = [
  {
    icon: <Glasses className="text-purple-400" />,
    title: 'Enter VR',
    description: 'Click "Show in VR" and put on your headset.'
  },
  {
    icon: <Gamepad2 className="text-blue-400" />,
    title: 'VR Interaction',
    description: 'Use your controllers to teleport and interact with objects.'
  },
  {
    icon: <Smartphone className="text-green-400" />,
    title: 'Enter AR',
    description: 'Tap "Show in AR" to view objects in your real environment.'
  }
]

export function Xyz3D()
{
  const isDebugging = isDebugMode

  return (
    <HelmetProvider>
      <WebCrafterProvider isDebugging={isDebugging}>
        <WebCrafterApp />
      </WebCrafterProvider>
    </HelmetProvider>
  )
}

function WebCrafterApp()
{
  const [ showPopup, setShowPopup ] = useState(false)
  const [ popupContent, setPopupContent ] = useState(null)
  const { isLoaded, isDebugging } = useWebCrafter()
  const ContextBridge = useContextBridge(WebCrafterContext)

  return (
    <>
      <div className="absolute inset-0 bg-black">
        <Canvas shadows="soft" dpr={[ 1, 2 ]} gl={GL_CONFIG}>
          <ContextBridge>
            <XR store={store}>
              <Suspense fallback={null}>
                <SceneXyz3D
                  path={SCENE_PATH}
                  setShowPopup={setShowPopup}
                  setPopupContent={setPopupContent}
                  isDebugging={isDebugging}
                />
              </Suspense>
              {isDebugging && <Perf />}
            </XR>
          </ContextBridge>
        </Canvas>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-50 flex gap-3">
          <button
            onClick={() => store.enterVR()}
            className="px-6 py-3 bg-white/90 text-black font-bold rounded-full shadow-lg hover:bg-white hover:scale-105 transition-all duration-200 backdrop-blur-sm flex items-center gap-2"
          >
            <Glasses className="w-5 h-5 text-purple-500" />
            Show in VR
          </button>

        </div>
      </div>

      {!isLoaded && <ProgressLoader />}

      {isLoaded && (
        <SplashScreen
          tutorial={TUTORIAL_STEPS}
          title="VR Experience"
          body="Immerse yourself in virtual reality."
          buttonText="Start"
        />
      )}

      {isLoaded && <Seo />}

      <HtmlOverlay
        content={popupContent}
        showPopup={showPopup}
        setShowPopup={setShowPopup}
      />

      {isLoaded && <OrbitNavigation />}
    </>
  )
}

export default Xyz3D
